import type {
  Atividade,
  AvisoChamada,
  AvisoDaSala,
  AvisoMencao,
  AvisoMensagem,
  AvisoNotificacao,
  MembroPresenca,
  MensagemDoServidor,
  PresencaDeMembro,
  DadoDaSala,
  SinalDaSala,
  SinalDeChamada,
  StatusEscolhido,
} from '@orbind/core'
import { apiOrigin, webTokenStorage } from '../../lib/api.ts'
import { plataformaDaSessao } from './atividade-do-sistema.ts'

/**
 * Conexao com o canal ao vivo de presenca.
 *
 * Fora do React de proposito: a conexao sobrevive a remontagem de componente,
 * e quem observa se inscreve. Uma conexao por aba — varias abas sao varias
 * sessoes, e e exatamente isso que o servidor conta.
 */

export type EventoPresenca =
  | { tipo: 'abertura'; voce: string; presencas: PresencaDeMembro[]; minutosAteAusente: number }
  | { tipo: 'configuracao'; minutosAteAusente: number }
  | { tipo: 'presenca'; presenca: PresencaDeMembro }
  | { tipo: 'perfil'; membro: MembroPresenca }
  | { tipo: 'chat'; channelId: string; messageId: string; acao: 'nova' | 'atualizada' }
  | { tipo: 'contato' }
  | { tipo: 'digitando'; channelId: string; memberId: string; nome: string }
  | AvisoMencao
  | AvisoChamada
  | AvisoDaSala
  | AvisoMensagem
  | AvisoNotificacao

type Ouvinte = (evento: EventoPresenca) => void

/** Backoff: reconectar em rajada castiga o servidor que acabou de voltar. */
const ESPERA_INICIAL_MS = 1_000
const ESPERA_MAXIMA_MS = 30_000

function urlDoCanal(): string {
  const origem = apiOrigin()
  // http -> ws, https -> wss. Trocar so o esquema preserva host e porta.
  return `${origem.replace(/^http/, 'ws')}/ws/presence`
}

class CanalDePresenca {
  private socket: WebSocket | null = null
  private ouvintes = new Set<Ouvinte>()
  private espera = ESPERA_INICIAL_MS
  private timer: ReturnType<typeof setTimeout> | null = null
  private encerrado = false
  /**
   * Última atividade informada — reenviada ao reconectar (SPEC-20260924-0924).
   *
   * O servidor não sabe o que esta sessão estava fazendo antes da queda: sem
   * reenviar, quem estava só no computador voltaria como se estivesse na
   * janela, e apareceria mais presente do que está.
   */
  private atividade: Atividade = 'no-orbind'
  /** Programa em uso junto com `no-computador` (SPEC-20261020-2341-programa-em-uso-na-presenca). */
  private programa: string | null = null

  /** A pessoa escolheu outro status no menu do perfil. */
  escolherStatus(status: StatusEscolhido) {
    this.enviar({ tipo: 'status', status })
  }

  /** O que esta sessão vê agora: janela, computador ou nada. */
  informarAtividade(atividade: Atividade, programa: string | null = null) {
    // o programa só faz sentido "no computador"
    const programaValido = atividade === 'no-computador' ? programa : null
    if (this.atividade === atividade && this.programa === programaValido) return
    this.atividade = atividade
    this.programa = programaValido
    this.enviar({ tipo: 'atividade', atividade, programa: programaValido })
  }

  /** Estou digitando nesta conversa. Quem chama espaça os avisos. */
  avisarDigitando(channelId: string) {
    this.enviar({ tipo: 'digitando', channelId })
  }

  /**
   * Sinal da videochamada para a outra pessoa. Devolve se saiu: sem socket
   * aberto a chamada não tem como tocar, e quem liga precisa saber na hora.
   */
  enviarSinalDeChamada(dados: { callId: string; channelId: string; para: string; sessao: string; sinal: SinalDeChamada }): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false
    this.socket.send(JSON.stringify({ tipo: 'chamada', ...dados }))
    return true
  }

  /* --------------------------------- sala com portaria (SPEC-20260917-0047) --- */

  entrarNaSala(roomId: string): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false
    this.socket.send(JSON.stringify({ tipo: 'sala-entrar', roomId }))
    return true
  }

  sairDaSala() {
    this.enviar({ tipo: 'sala-sair' })
  }

  /** Sinal de mídia para OUTRO participante da sala. */
  enviarSinalDaSala(para: string, sinal: SinalDaSala): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false
    this.socket.send(JSON.stringify({ tipo: 'sala', para, sinal }))
    return true
  }

  /** Conta algo para a sala inteira: estado, chat da reunião, reação (SPEC-20261010-0157). */
  contarParaASala(dado: DadoDaSala): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false
    this.socket.send(JSON.stringify({ tipo: 'sala-dado', dado }))
    return true
  }

  /** Deixa entrar (ou barra) quem bateu na porta. Só vale para o dono da sala. */
  decidirNaPortaria(guestId: string, aprovar: boolean) {
    this.enviar({ tipo: 'sala-decidir', guestId, aprovar })
  }

  private enviar(payload: unknown) {
    // Socket fechado no meio de uma queda: o estado atual vai de novo no
    // proximo `onopen`, entao perder este quadro nao deixa nada inconsistente.
    if (this.socket?.readyState !== WebSocket.OPEN) return
    this.socket.send(JSON.stringify(payload))
  }

  inscrever(ouvinte: Ouvinte): () => void {
    this.ouvintes.add(ouvinte)
    this.garantirConexao()
    return () => {
      this.ouvintes.delete(ouvinte)
      // Sem ninguem ouvindo (logout, por exemplo) a conexao nao precisa viver.
      if (this.ouvintes.size === 0) this.encerrar()
    }
  }

  private emitir(evento: EventoPresenca) {
    for (const ouvinte of this.ouvintes) ouvinte(evento)
  }

  private garantirConexao() {
    if (this.socket !== null || this.encerrado) return
    const token = webTokenStorage.get()
    // Sem sessao nao ha o que ouvir; quando o login acontecer, o provedor
    // remonta e chama de novo.
    if (!token) return

    this.encerrado = false
    const socket = new WebSocket(urlDoCanal())
    this.socket = socket

    socket.onopen = () => {
      // O token vai no PRIMEIRO QUADRO, nunca na URL: query string entra em
      // log de servidor e de proxy.
      socket.send(JSON.stringify({ tipo: 'autenticar', token, plataforma: plataformaDaSessao() }))
      // Reconectou depois de uma queda: o servidor nao sabe o que esta sessao
      // estava fazendo, entao reenviamos o que sabemos.
      if (this.atividade !== 'no-orbind') {
        socket.send(JSON.stringify({ tipo: 'atividade', atividade: this.atividade, programa: this.programa }))
      }
    }

    socket.onmessage = (evento) => {
      // Socket antigo que ainda nao terminou de fechar nao fala mais pela aba.
      if (this.socket !== socket) return
      let mensagem: MensagemDoServidor
      try {
        mensagem = JSON.parse(String(evento.data)) as MensagemDoServidor
      } catch {
        return
      }
      // Conexao que chegou a receber dados e uma conexao saudavel: zera o
      // backoff para a proxima queda reconectar rapido.
      this.espera = ESPERA_INICIAL_MS
      if (mensagem.tipo === 'abertura') {
        this.emitir({
          tipo: 'abertura',
          voce: mensagem.voce,
          presencas: [...mensagem.presencas],
          minutosAteAusente: mensagem.minutosAteAusente,
        })
      } else {
        this.emitir(mensagem)
      }
    }

    socket.onclose = () => {
      // O close de um socket JA SUBSTITUIDO chega atrasado (o StrictMode fecha
      // a primeira conexao ainda conectando). Sem esta guarda ele zerava a
      // referencia do socket novo, a reconexao abria um terceiro, e a aba
      // ficava com duas sessoes vivas — cada aviso chegava duas vezes.
      if (this.socket !== socket) return
      this.socket = null
      if (this.encerrado || this.ouvintes.size === 0) return
      this.agendarReconexao()
    }

    socket.onerror = () => {
      // O close vem logo atras e cuida da reconexao; fechar aqui tambem
      // duplicaria o agendamento.
      socket.close()
    }
  }

  private agendarReconexao() {
    if (this.timer !== null) return
    const espera = this.espera
    this.espera = Math.min(espera * 2, ESPERA_MAXIMA_MS)
    this.timer = setTimeout(() => {
      this.timer = null
      this.garantirConexao()
    }, espera)
  }

  private encerrar() {
    this.encerrado = true
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.socket?.close()
    this.socket = null
    this.espera = ESPERA_INICIAL_MS
  }

  /** Chamado no login: reabre depois de um encerramento por logout. */
  reabrir() {
    this.encerrado = false
    this.espera = ESPERA_INICIAL_MS
    this.garantirConexao()
  }
}

export const canalDePresenca = new CanalDePresenca()
